/**
 * The Back Roads detail layer, decoded.
 *
 * `public/backroads/detail.json` is written by `packages/data/scripts/fetchnydetail.ts`
 * in a compact form: every coordinate is an integer at 1/10,000 of a degree,
 * and every coordinate after the first in a line or ring is a delta from the
 * one before. That is a third the size of the same shapes as GeoJSON, which
 * matters for a file that a phone downloads and parses on the first trip.
 *
 * Decoding is a loop, and the output is plain GeoJSON the map can feed
 * straight into a source: one MultiLineString per road class, one for the
 * rivers, one MultiPolygon of every lake, one MultiPoint per settlement size.
 */

import type { Feature, FeatureCollection, MultiLineString, MultiPoint, MultiPolygon, Position } from 'geojson';

/** The file's shape. `scale` is the integer units per degree. */
export interface DetailFile {
  readonly v: 1;
  readonly scale: number;
  /** By class: 1 trunk or primary, 2 secondary. Each entry is one delta-encoded line. */
  readonly roads: { readonly 1: readonly (readonly number[])[]; readonly 2: readonly (readonly number[])[] };
  readonly rivers: readonly (readonly number[])[];
  /** Each polygon is its rings, outer first; each ring delta-encoded. */
  readonly water: readonly (readonly (readonly number[])[])[];
  /** By size: 3 city, 2 town or village, 1 hamlet. Flat delta-encoded points. */
  readonly places: { readonly 1: readonly number[]; readonly 2: readonly number[]; readonly 3: readonly number[] };
}

export interface DecodedDetail {
  readonly water: FeatureCollection;
  readonly rivers: FeatureCollection;
  readonly roads: FeatureCollection;
  readonly places: FeatureCollection;
}

/** A delta-encoded run of integer pairs back to [lng, lat] positions. */
export function decodeLine(ints: readonly number[], scale: number): Position[] {
  const out: Position[] = [];
  let x = 0;
  let y = 0;
  for (let i = 0; i + 1 < ints.length; i += 2) {
    x += ints[i] as number;
    y += ints[i + 1] as number;
    out.push([x / scale, y / scale]);
  }
  return out;
}

/** The inverse, for the bake and for the round-trip test. */
export function encodeLine(line: readonly Position[], scale: number): number[] {
  const out: number[] = [];
  let px = 0;
  let py = 0;
  for (const p of line) {
    const x = Math.round((p[0] as number) * scale);
    const y = Math.round((p[1] as number) * scale);
    out.push(x - px, y - py);
    px = x;
    py = y;
  }
  return out;
}

function multiLine(lines: readonly (readonly number[])[], scale: number, props: Record<string, unknown>): Feature<MultiLineString> {
  return {
    type: 'Feature',
    properties: props,
    geometry: { type: 'MultiLineString', coordinates: lines.map((l) => decodeLine(l, scale)) },
  };
}

export function decodeDetail(file: DetailFile): DecodedDetail {
  const s = file.scale;
  const water: Feature<MultiPolygon> = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'MultiPolygon',
      coordinates: file.water.map((rings) => rings.map((r) => decodeLine(r, s))),
    },
  };
  const places: Feature<MultiPoint>[] = ([1, 2, 3] as const).map((size) => ({
    type: 'Feature',
    properties: { s: size },
    geometry: { type: 'MultiPoint', coordinates: decodeLine(file.places[size], s) },
  }));
  return {
    water: { type: 'FeatureCollection', features: [water] },
    rivers: { type: 'FeatureCollection', features: [multiLine(file.rivers, s, {})] },
    roads: {
      type: 'FeatureCollection',
      features: [multiLine(file.roads[1], s, { c: 1 }), multiLine(file.roads[2], s, { c: 2 })],
    },
    places: { type: 'FeatureCollection', features: places },
  };
}

/** A cheap shape check before decoding a file a stranger could have replaced. */
export function isDetailFile(value: unknown): value is DetailFile {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  const roads = v['roads'] as Record<string, unknown> | undefined;
  const places = v['places'] as Record<string, unknown> | undefined;
  return (
    v['v'] === 1 &&
    typeof v['scale'] === 'number' &&
    Array.isArray(v['rivers']) &&
    Array.isArray(v['water']) &&
    !!roads &&
    Array.isArray(roads['1']) &&
    Array.isArray(roads['2']) &&
    !!places &&
    Array.isArray(places['1']) &&
    Array.isArray(places['2']) &&
    Array.isArray(places['3'])
  );
}
